/**
 * Som do Tigrinho Tour.
 *
 * - Efeitos: sintetizados na hora com Web Audio (nada para baixar, resposta instantânea).
 * - Trilha: rock em 4 camadas (violão → banda → guitarras pesadas → solo), sintetizada em rock.ts.
 * - Locutor: frases gravadas (geradas por IA) em /audio/locutor/*.mp3.
 *
 * O navegador só libera som depois do primeiro toque na tela; até lá tudo fica em silêncio.
 */
import { ROCK_STEPS, RockBand } from './rock';
import { prefs } from './storage';

export type Sfx =
  | 'dice'
  | 'step'
  | 'whoosh'
  | 'coin'
  | 'coinDown'
  | 'cash'
  | 'facada'
  | 'mortal'
  | 'card'
  | 'throw'
  | 'splat'
  | 'build'
  | 'hotel'
  | 'bankrupt'
  | 'jail'
  | 'turn'
  | 'tick'
  | 'tension'
  | 'win'
  | 'shield'
  | 'boom'
  | 'tap';

export type VoiceLine =
  | 'facada'
  | 'mortal'
  | 'quebrou'
  | 'cadeia'
  | 'hotel'
  | 'grupo'
  | 'retaFinal'
  | 'ultimaRodada'
  | 'caos'
  | 'vitoria'
  | 'inicio'
  | 'tensao2'
  | 'tensao3'
  | 'tensao4'
  | 'derrubou';

type Level = 1 | 2 | 3 | 4;

/**
 * Falas do locutor que já existem em public/audio/locutor. Fica vazio até as gravações chegarem;
 * assim o jogo nunca tenta baixar um arquivo que não existe.
 */
export const VOICE_FILES: Partial<Record<VoiceLine, string[]>> = {};

const NOTE = (midi: number) => 440 * 2 ** ((midi - 69) / 12);

/** Volume da trilha (o resto do jogo fica por cima). */
const MUSIC_GAIN = 0.5;

class AudioEngine {
  private ctx: BaseAudioContext | null = null;
  private master!: GainNode;
  private sfxBus!: GainNode;
  private musicBus!: GainNode;
  private voiceBus!: GainNode;
  private comp!: DynamicsCompressorNode;
  private noise!: AudioBuffer;
  private voices = new Map<string, Promise<AudioBuffer | null>>();
  private lastVoiceAt = 0;
  private lastSfx: Partial<Record<Sfx, number>> = {};

  // Trilha
  private level: Level = 1;
  private playing = false;
  private band: RockBand | null = null;
  private nextStepTime = 0;
  private step = 0;
  private timer: ReturnType<typeof setInterval> | null = null;

  /** Cria o contexto de áudio no primeiro gesto da pessoa (exigência dos navegadores). */
  unlock(): void {
    if (typeof window === 'undefined') return;
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    if (!this.ctx) {
      this.init(new Ctx({ latencyHint: 'interactive' }));
      this.applyPrefs();
      document.addEventListener('visibilitychange', () => {
        const c = this.ctx as AudioContext | null;
        if (!c) return;
        if (document.hidden) void c.suspend();
        else void c.resume();
      });
    }
    if (this.ctx?.state === 'suspended') void (this.ctx as AudioContext).resume();
  }

  /** Monta a mixagem (barramentos, compressor, ruído e camadas da trilha) num contexto de áudio. */
  private init(ctx: BaseAudioContext): void {
    this.ctx = ctx;
    this.comp = ctx.createDynamicsCompressor();
    this.comp.threshold.value = -14;
    this.comp.ratio.value = 4;
    this.master = ctx.createGain();
    this.master.gain.value = 0.9;
    this.comp.connect(this.master).connect(ctx.destination);
    this.sfxBus = ctx.createGain();
    this.musicBus = ctx.createGain();
    this.voiceBus = ctx.createGain();
    this.sfxBus.connect(this.comp);
    this.musicBus.connect(this.comp);
    this.voiceBus.connect(this.master); // voz por fora do compressor, para ficar na frente
    this.voiceBus.gain.value = 1.0;
    this.noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    this.band = new RockBand(ctx, this.musicBus, this.noise);
  }

  /**
   * Grava a trilha de um nível de tensão num arquivo (para o Matheus ouvir e aprovar).
   * Usa um contexto "offline": renderiza mais rápido que o tempo real, sem tocar nada.
   */
  static async renderMusic(level: Level, seconds: number): Promise<AudioBuffer> {
    const rate = 44100;
    const off = new OfflineAudioContext(2, Math.ceil(rate * seconds), rate);
    const e = new AudioEngine();
    e.init(off);
    e.musicBus.gain.value = MUSIC_GAIN * 1.8; // sem o resto do jogo, a trilha pode ir mais alta
    e.level = level;
    const band = e.band!;
    band.warm();
    band.setLevel(level, 0, 0);
    for (let s = 0, t = 0.05; t < seconds - 0.5; s++, t += band.stepDur) band.playStep(s % ROCK_STEPS, t);
    return off.startRendering();
  }

  get ready(): boolean {
    return !!this.ctx && this.ctx.state === 'running';
  }

  applyPrefs(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const all = !prefs.muted();
    const vol = prefs.volume();
    this.sfxBus.gain.setTargetAtTime(all && prefs.soundFx() ? 0.95 * vol : 0, t, 0.05);
    this.musicBus.gain.setTargetAtTime(all && prefs.music() ? this.musicLevel() : 0, t, 0.2);
    this.voiceBus.gain.setTargetAtTime(all && prefs.voice() ? 1.1 * vol : 0, t, 0.05);
  }

  /** Volume da trilha (o controle "Música" vai de 0 a 1; o meio fica no volume de antes). */
  private musicLevel(): number {
    return MUSIC_GAIN * 1.6 * prefs.musicVolume() ** 1.5;
  }

  // ------------------------------------------------------------------ peças básicas

  private env(g: GainNode, t: number, peak: number, attack: number, decay: number): void {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  }

  private tone(
    out: AudioNode,
    freq: number,
    t: number,
    dur: number,
    peak: number,
    type: OscillatorType = 'sine',
    glideTo?: number,
    attack = 0.005,
  ): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (glideTo) o.frequency.exponentialRampToValueAtTime(glideTo, t + dur);
    this.env(g, t, peak, attack, dur);
    o.connect(g).connect(out);
    o.start(t);
    o.stop(t + attack + dur + 0.05);
  }

  private hiss(
    out: AudioNode,
    t: number,
    dur: number,
    peak: number,
    filter: BiquadFilterType,
    f0: number,
    f1?: number,
    q = 1,
  ): void {
    const ctx = this.ctx!;
    const s = ctx.createBufferSource();
    s.buffer = this.noise;
    const f = ctx.createBiquadFilter();
    f.type = filter;
    f.Q.value = q;
    f.frequency.setValueAtTime(f0, t);
    if (f1) f.frequency.exponentialRampToValueAtTime(f1, t + dur);
    const g = ctx.createGain();
    this.env(g, t, peak, 0.004, dur);
    s.connect(f).connect(g).connect(out);
    s.start(t, Math.random() * 0.5);
    s.stop(t + dur + 0.05);
  }

  // ------------------------------------------------------------------ efeitos

  sfx(name: Sfx, delay = 0): void {
    if (!this.ready || prefs.muted() || !prefs.soundFx()) return;
    const ctx = this.ctx!;
    const now = ctx.currentTime;
    // Evita empilhar o mesmo som no mesmo instante (ex.: vários eventos juntos).
    const last = this.lastSfx[name] ?? 0;
    if (name !== 'step' && name !== 'tick' && now + delay - last < 0.06) return;
    this.lastSfx[name] = now + delay;
    const t = now + delay + 0.01;
    const o = this.sfxBus;
    switch (name) {
      case 'tap':
        this.tone(o, 900, t, 0.04, 0.08, 'sine', 1200);
        break;
      case 'dice':
        for (let i = 0; i < 7; i++) {
          const ti = t + i * 0.055 + Math.random() * 0.03;
          this.hiss(o, ti, 0.045, 0.32 - i * 0.03, 'bandpass', 2600 + Math.random() * 1800, undefined, 3);
          this.tone(o, 180 + Math.random() * 120, ti, 0.05, 0.12, 'triangle');
        }
        this.tone(o, 120, t + 0.42, 0.12, 0.3, 'sine', 60);
        break;
      case 'step':
        this.tone(o, 520, t, 0.06, 0.13, 'triangle', 760);
        this.hiss(o, t, 0.03, 0.05, 'highpass', 5000);
        break;
      case 'whoosh':
        this.hiss(o, t, 0.5, 0.35, 'bandpass', 300, 3200, 2);
        this.tone(o, 300, t, 0.5, 0.08, 'sine', 900);
        break;
      case 'coin':
        this.tone(o, NOTE(83), t, 0.07, 0.18, 'square');
        this.tone(o, NOTE(88), t + 0.07, 0.28, 0.16, 'square');
        break;
      case 'coinDown':
        this.tone(o, NOTE(84), t, 0.07, 0.14, 'square');
        this.tone(o, NOTE(79), t + 0.08, 0.07, 0.13, 'square');
        this.tone(o, NOTE(72), t + 0.16, 0.22, 0.12, 'square');
        break;
      case 'cash':
        this.hiss(o, t, 0.09, 0.4, 'highpass', 3000);
        this.tone(o, 110, t, 0.08, 0.3, 'square', 70);
        for (const [f, d] of [
          [NOTE(91), 0.9],
          [NOTE(96), 0.8],
          [NOTE(100), 0.6],
        ] as const)
          this.tone(o, f, t + 0.08, d, 0.11, 'triangle');
        break;
      case 'facada': {
        this.tone(o, 95, t, 0.45, 0.6, 'sine', 38);
        this.hiss(o, t, 0.35, 0.45, 'lowpass', 2400, 300);
        // Acorde menor descendo, tipo "tchan tchan tchaaan".
        const chord = [57, 60, 64];
        for (const [k, base] of [
          [0, 0],
          [1, -1],
          [2, -2],
        ] as const)
          for (const n of chord) this.tone(o, NOTE(n + base), t + k * 0.22, k === 2 ? 0.9 : 0.18, 0.07, 'sawtooth');
        break;
      }
      case 'mortal':
        this.sfx('facada', delay);
        this.hiss(o, t + 0.05, 1.2, 0.35, 'highpass', 6000, 2500);
        this.tone(o, 60, t + 0.66, 1.2, 0.5, 'sine', 30);
        break;
      case 'card':
        this.hiss(o, t, 0.32, 0.3, 'bandpass', 600, 4000, 1.5);
        this.tone(o, 1400, t + 0.34, 0.05, 0.1, 'triangle');
        this.tone(o, NOTE(76), t + 0.4, 0.3, 0.09, 'triangle');
        this.tone(o, NOTE(83), t + 0.48, 0.4, 0.09, 'triangle');
        break;
      case 'throw':
        this.hiss(o, t, 0.4, 0.25, 'bandpass', 1500, 400, 2);
        break;
      case 'splat':
        this.hiss(o, t, 0.25, 0.6, 'lowpass', 1400, 200);
        this.tone(o, 140, t, 0.18, 0.4, 'sine', 50);
        break;
      case 'build':
        [60, 64, 67, 72].forEach((n, i) => this.tone(o, NOTE(n + 12), t + i * 0.06, 0.18, 0.12, 'triangle'));
        this.tone(o, 90, t, 0.1, 0.25, 'sine', 60);
        break;
      case 'hotel': {
        const seq = [60, 64, 67, 72, 76, 79, 84];
        seq.forEach((n, i) => this.tone(o, NOTE(n + 12), t + i * 0.07, 0.2, 0.1, 'square'));
        for (const n of [72, 76, 79, 84]) this.tone(o, NOTE(n), t + 0.5, 1.2, 0.06, 'sawtooth');
        this.hiss(o, t + 0.5, 1.0, 0.12, 'highpass', 8000);
        break;
      }
      case 'bankrupt': {
        // Trombone triste: "uón uón uón uóóón".
        const notes = [67, 66, 65, 64];
        notes.forEach((n, i) => {
          const ti = t + i * 0.42;
          const dur = i === 3 ? 1.1 : 0.36;
          const osc = this.ctx!.createOscillator();
          const f = this.ctx!.createBiquadFilter();
          const g = this.ctx!.createGain();
          osc.type = 'sawtooth';
          osc.frequency.setValueAtTime(NOTE(n - 12), ti);
          if (i === 3) {
            const lfo = this.ctx!.createOscillator();
            const lg = this.ctx!.createGain();
            lfo.frequency.value = 6;
            lg.gain.value = 6;
            lfo.connect(lg).connect(osc.frequency);
            lfo.start(ti);
            lfo.stop(ti + dur + 0.1);
          }
          f.type = 'lowpass';
          f.Q.value = 6;
          f.frequency.setValueAtTime(400, ti);
          f.frequency.linearRampToValueAtTime(1400, ti + 0.12);
          f.frequency.linearRampToValueAtTime(500, ti + dur);
          this.env(g, ti, 0.22, 0.03, dur);
          osc.connect(f).connect(g).connect(o);
          osc.start(ti);
          osc.stop(ti + dur + 0.1);
        });
        break;
      }
      case 'jail':
        for (const [f, p] of [
          [523, 0.25],
          [1187, 0.14],
          [1789, 0.1],
          [2633, 0.06],
        ] as const)
          this.tone(o, f, t, 1.1, p, 'sine');
        for (let i = 0; i < 4; i++) this.tone(o, i % 2 ? 660 : 880, t + 0.2 + i * 0.22, 0.2, 0.08, 'square');
        break;
      case 'turn':
        this.tone(o, NOTE(79), t, 0.16, 0.16, 'triangle');
        this.tone(o, NOTE(86), t + 0.12, 0.35, 0.16, 'triangle');
        break;
      case 'tick':
        this.tone(o, 1800, t, 0.03, 0.12, 'square');
        break;
      case 'tension':
        this.hiss(o, t, 1.6, 0.3, 'bandpass', 200, 5000, 3);
        this.tone(o, 55, t + 1.5, 1.2, 0.55, 'sine', 35);
        this.hiss(o, t + 1.5, 0.8, 0.3, 'lowpass', 1800, 200);
        break;
      case 'win': {
        const melody = [72, 72, 72, 76, 79, 76, 79, 84];
        const times = [0, 0.14, 0.28, 0.42, 0.7, 0.98, 1.12, 1.4];
        melody.forEach((n, i) => this.tone(o, NOTE(n), t + times[i]!, i === 7 ? 1.4 : 0.16, 0.12, 'square'));
        for (const n of [60, 64, 67, 72]) this.tone(o, NOTE(n), t + 1.4, 1.6, 0.06, 'sawtooth');
        this.hiss(o, t + 1.4, 1.4, 0.15, 'highpass', 7000);
        break;
      }
      case 'shield':
        this.tone(o, NOTE(84), t, 0.5, 0.12, 'sine');
        this.tone(o, NOTE(91), t + 0.05, 0.6, 0.1, 'sine');
        this.hiss(o, t, 0.3, 0.1, 'highpass', 6000);
        break;
      case 'boom':
        this.tone(o, 80, t, 0.7, 0.6, 'sine', 30);
        this.hiss(o, t, 0.9, 0.5, 'lowpass', 3000, 120);
        break;
    }
  }

  // ------------------------------------------------------------------ locutor

  voice(line: VoiceLine, delay = 0): void {
    const files = VOICE_FILES[line];
    if (!files?.length || !this.ready || prefs.muted() || !prefs.voice()) return;
    // Várias gravações da mesma fala (amigos diferentes): sorteia uma.
    const file = files[Math.floor(Math.random() * files.length)]!;
    const ctx = this.ctx!;
    // Um locutor só de cada vez, sem atropelar.
    if (ctx.currentTime < this.lastVoiceAt) return;
    let p = this.voices.get(file);
    if (!p) {
      p = fetch(`/audio/locutor/${file}.mp3`)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
        .then((b) => ctx.decodeAudioData(b))
        .catch(() => null);
      this.voices.set(file, p);
    }
    const start = ctx.currentTime + delay;
    this.lastVoiceAt = start + 1.2;
    void p.then((buf) => {
      if (!buf || !this.ctx) return;
      const s = this.ctx.createBufferSource();
      s.buffer = buf;
      // Rádio de carro de som: corta graves e agudos.
      const hp = this.ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 160;
      s.connect(hp).connect(this.voiceBus);
      const at = Math.max(this.ctx.currentTime, start);
      s.start(at);
      this.lastVoiceAt = at + buf.duration + 0.3;
      this.duck(at, buf.duration);
    });
  }

  /** Abaixa a trilha enquanto o locutor fala. */
  private duck(at: number, dur: number): void {
    const g = this.musicBus.gain;
    const base = !prefs.muted() && prefs.music() ? this.musicLevel() : 0;
    g.cancelScheduledValues(at);
    g.setTargetAtTime(base * 0.35, at, 0.05);
    g.setTargetAtTime(base, at + dur, 0.3);
  }

  /** Baixa as falas antes de precisar delas. */
  preloadVoices(lines: VoiceLine[]): void {
    const ctx = this.ctx;
    if (!ctx) return;
    for (const l of lines.flatMap((x) => VOICE_FILES[x] ?? [])) {
      if (this.voices.has(l)) continue;
      this.voices.set(
        l,
        fetch(`/audio/locutor/${l}.mp3`)
          .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
          .then((b) => ctx.decodeAudioData(b))
          .catch(() => null),
      );
    }
  }

  // ------------------------------------------------------------------ trilha (rock em camadas)

  startMusic(level: Level): void {
    if (!this.ctx || !this.band) return;
    if (!this.playing) this.band.warm();
    this.setLevel(level);
    if (this.playing) return;
    this.playing = true;
    this.step = 0;
    this.nextStepTime = this.ctx.currentTime + 0.1;
    this.timer = setInterval(() => this.schedule(), 40);
  }

  stopMusic(): void {
    this.playing = false;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (this.ctx && this.band) for (const g of this.band.layers) g.gain.setTargetAtTime(0, this.ctx.currentTime, 0.4);
  }

  setLevel(level: Level): void {
    this.level = level;
    if (!this.ctx || !this.band) return;
    this.band.setLevel(level, this.ctx.currentTime);
  }

  private schedule(): void {
    const ctx = this.ctx;
    const band = this.band;
    if (!ctx || !band || !this.playing) return;
    while (this.nextStepTime < ctx.currentTime + 0.18) {
      band.playStep(this.step, this.nextStepTime);
      this.nextStepTime += band.stepDur;
      this.step = (this.step + 1) % ROCK_STEPS;
    }
  }
}

export const audio = new AudioEngine();

/** Liga o som no primeiro toque/tecla da pessoa (só funciona depois de um gesto). */
export function installAudioUnlock(): void {
  if (typeof document === 'undefined') return;
  const go = () => {
    audio.unlock();
    if (audio.ready) {
      document.removeEventListener('pointerdown', go, true);
      document.removeEventListener('keydown', go, true);
    }
  };
  document.addEventListener('pointerdown', go, true);
  document.addEventListener('keydown', go, true);
}

/** Grava um nível da trilha e devolve um WAV (16 bits) em base64. */
export async function renderMusicWav(level: Level, seconds: number): Promise<string> {
  const buf = await (AudioEngine as unknown as { renderMusic: (l: Level, s: number) => Promise<AudioBuffer> }).renderMusic(level, seconds);
  const ch = [buf.getChannelData(0), buf.getChannelData(1)];
  const n = buf.length;
  const out = new DataView(new ArrayBuffer(44 + n * 4));
  const w = (o: number, s: string) => [...s].forEach((c, i) => out.setUint8(o + i, c.charCodeAt(0)));
  w(0, 'RIFF');
  out.setUint32(4, 36 + n * 4, true);
  w(8, 'WAVE');
  w(12, 'fmt ');
  out.setUint32(16, 16, true);
  out.setUint16(20, 1, true);
  out.setUint16(22, 2, true);
  out.setUint32(24, buf.sampleRate, true);
  out.setUint32(28, buf.sampleRate * 4, true);
  out.setUint16(32, 4, true);
  out.setUint16(34, 16, true);
  w(36, 'data');
  out.setUint32(40, n * 4, true);
  for (let i = 0; i < n; i++)
    for (let c = 0; c < 2; c++) out.setInt16(44 + i * 4 + c * 2, Math.max(-1, Math.min(1, ch[c]![i]!)) * 0x7fff, true);
  const bytes = new Uint8Array(out.buffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}
